import { onAuthStateChanged } from 'firebase/auth';
import { useMemo, useEffect, useCallback } from 'react';
import {doc, getDoc, setDoc, serverTimestamp} from 'firebase/firestore';

import { useSetState } from 'src/hooks/use-set-state';

import axios from 'src/utils/axios';

import { AUTH, FIRESTORE } from 'src/lib/firebase';

import { AuthContext } from '../auth-context';
import { normalizeGroupIds } from '../../groups';
import { isUserRole, DEFAULT_ROLE } from '../../roles';

import type { AuthState } from '../../types';

// ----------------------------------------------------------------------

/**
 * NOTE:
 * We only build demo at basic level.
 * Customer will need to do some extra handling yourself if you want to extend the logic and other features...
 */

type Props = {
  children: React.ReactNode;
};

export function AuthProvider({ children }: Props) {
  const { state, setState } = useSetState<AuthState>({
    user: null,
    loading: true,
  });

  const checkUserSession = useCallback(async () => {
    try {
      onAuthStateChanged(AUTH, async (user: AuthState['user']) => {
        const isOAuthProvider = user?.providerData.some(
          (p: { providerId: string; }) => p.providerId === 'microsoft.com'
        ) ?? false;
        if (user && (user.emailVerified || isOAuthProvider)) {
          /*
           * (1) If skip emailVerified
           * Remove the condition (if/else) : user.emailVerified
           */
          const userProfile = doc(FIRESTORE, 'users', user.uid);

          const docSnap = await getDoc(userProfile);

          let profileData = docSnap.data();

          // Auto-provision a users/{uid} doc on first sign-in (e.g. Microsoft OAuth).
          if (!docSnap.exists()) {
            const newProfile = {
              uid: user.uid,
              email: user.email ?? '',
              displayName: user.displayName ?? '',
              role: DEFAULT_ROLE,
              groups: [] as string[],
              createdAt: serverTimestamp(),
            };
            try {
              await setDoc(userProfile, newProfile);
            } catch (err) {
              console.error('Failed to provision user profile', err);
            }
            profileData = newProfile;
          }

          const { accessToken } = user;

          setState({ user: { ...user, ...profileData }, loading: false });
          axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;
        } else {
          setState({ user: null, loading: false });
          delete axios.defaults.headers.common.Authorization;
        }
      });
    } catch (error) {
      console.error(error);
      setState({ user: null, loading: false });
    }
  }, [setState]);

  useEffect(() => {
    checkUserSession();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ----------------------------------------------------------------------

  const checkAuthenticated = state.user ? 'authenticated' : 'unauthenticated';

  const status = state.loading ? 'loading' : checkAuthenticated;

  const memoizedValue = useMemo(
    () => ({
      user: state.user
        ? {
            ...state.user,
            id: state.user?.uid,
            accessToken: state.user?.accessToken,
            displayName: state.user?.displayName,
            photoURL: state.user?.photoURL,
            role: isUserRole(state.user?.role) ? state.user.role : DEFAULT_ROLE,
            groups: normalizeGroupIds(state.user?.groups),
          }
        : null,
      checkUserSession,
      loading: status === 'loading',
      authenticated: status === 'authenticated',
      unauthenticated: status === 'unauthenticated',
    }),
    [checkUserSession, state.user, status]
  );

  return <AuthContext.Provider value={memoizedValue}>{children}</AuthContext.Provider>;
}
